import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { app } from '@microsoft/teams-js';
import { makeActiveConversationsPageStyles } from './ActiveConversationsPage.styles';
import { useAppDispatch, useSelector } from '../../store/hooks';
import { useActiveConversationsSignalR } from '../../hooks/useActiveConversationsSignalR';
import {
    loadActiveConversations,
    selectActiveConversations,
    setTeamFilter,
    selectActiveConversationsPagination,
    selectConversationNotes,
    setCurrentPage,
    loadConversationNotes,
    addConversationNoteThunk,
    removeConversationNoteThunk,
} from '../../store/features/activeConversations/activeConversationsSlice';
import { loadConfig } from '../../store/features/config/configSlice';
import { ActiveConversationsToolbar } from './ActiveConversationsToolbar/ActiveConversationsToolbar';
import { ActiveConversationsTable } from './ActiveConversationsTable/ActiveConversationsTable';
import PaginationBar from '../PaginationBar';
import ToastNotification from './ToastNotification/ToastNotification';
import { useAppContext } from '../../model';
import { checkIsArrivingSoon } from '../../utils/activeConversationsUtils';
import { ConversationNotesDialog } from './ConversationNotes/ConversationNotesDialog';
import TransferConversationDialog from './TransferConversation/TransferConversationDialog';
import { useValueDialog } from '../../hooks/useDialog';
import { IBrokerConnectionActiveConversationsData } from '../../model/brokerConnections/IBrokerConnectionActiveConversationsData';
import { useActiveConversationsPreferences } from '../../hooks/useActiveConversationsPreferences';

const POLL_INTERVAL_MS = 60000; // 1 minute

const ActiveConversationsPage: React.FC = () => {
    const styles = makeActiveConversationsPageStyles();
    const dispatch = useAppDispatch();
    const [teamId, setTeamId] = useState<string | null>(null);
    const [notesModalOpen, setNotesModalOpen] = useState(false);
    const [selectedConversationId, setSelectedConversationId] = useState<
        string | null
    >(null);
    const { teamsContext } = useAppContext();
    const {
        items,
        loadStatus,
        teamIdFilter,
        unreadOnly,
        searchFlightQuery,
        statusFilter,
        initiatedFromFilter,
        awaitingReplyOnly,
        arrivingSoonOnly,
        error,
    } = useSelector(selectActiveConversations);
    const pagination = useSelector(selectActiveConversationsPagination);
    const conversationNotes = useSelector(selectConversationNotes);
    const selectedConversation = selectedConversationId
        ? items.find((item) => item.id === selectedConversationId)
        : undefined;
    const preferencesTeamId = teamIdFilter ?? teamId;
    const [preferences, setPreferences, preferencesLoaded] =
        useActiveConversationsPreferences(preferencesTeamId);

    const handleGroupByStatusChange = useCallback(
        (value: boolean) => {
            setPreferences((previous) => ({
                ...previous,
                groupByStatus: value,
            }));
        },
        [setPreferences]
    );

    const handleConversationNotesClick = useCallback(
        (conversationId: string) => {
            setSelectedConversationId(conversationId);
            setNotesModalOpen(true);
            dispatch(loadConversationNotes(conversationId));
        },
        [dispatch]
    );

    const handleAddConversationNote = useCallback(
        async (note: string) => {
            if (!selectedConversationId) return;

            await dispatch(
                addConversationNoteThunk({
                    conversationId: selectedConversationId,
                    note,
                })
            ).unwrap();
        },
        [dispatch, selectedConversationId]
    );

    const handleDeleteConversationNote = useCallback(
        async (noteId: string) => {
            if (!selectedConversationId) return;

            await dispatch(
                removeConversationNoteThunk({
                    conversationId: selectedConversationId,
                    noteId,
                })
            ).unwrap();
        },
        [dispatch, selectedConversationId]
    );

    // Enable SignalR for real-time updates
    const { isConnected } = useActiveConversationsSignalR(teamIdFilter, true);

    useEffect(() => {
        let cancelled = false;
        const init = async () => {
            try {
                if (cancelled) return;
                const teamId =
                    teamsContext?.team?.groupId ||
                    teamsContext?.team?.internalId ||
                    null;
                setTeamId(teamId);
                dispatch(setTeamFilter(teamId));
                dispatch(loadActiveConversations());
                dispatch(loadConfig());
            } catch (e) {
                console.error('Failed to get Teams context', e);
                // Load without team filter as fallback
                dispatch(loadActiveConversations());
                dispatch(loadConfig());
            }
        };
        init();
        return () => {
            cancelled = true;
        };
    }, [dispatch, teamsContext]);

    // Poll if SignalR is not connected as fallback
    useEffect(() => {
        if (isConnected) {
            return;
        }
        const poll = () => {
            // Avoid stacking requests if a load is in progress
            if (
                document.visibilityState === 'visible' &&
                loadStatus !== 'loading'
            ) {
                dispatch(loadActiveConversations());
            }
        };
        const intervalId = setInterval(poll, POLL_INTERVAL_MS);
        return () => clearInterval(intervalId);
    }, [dispatch, loadStatus, teamIdFilter, isConnected]);

    useEffect(() => {
        if (loadStatus === 'idle') {
            app.notifySuccess();
        }
    }, [loadStatus]);

    const filteredItems = useMemo(() => {
        let result = items;
        if (unreadOnly) {
            result = result.filter((i) => !i.isRead);
        }
        if (searchFlightQuery) {
            const q = searchFlightQuery.trim().toLowerCase();
            result = result.filter(
                (i) =>
                    i.iataFlightIdentifier?.toLowerCase().includes(q) ||
                    i.flightNumber?.toLowerCase().includes(q)
            );
        }
        if (statusFilter) {
            const s = statusFilter.toLowerCase();
            result = result.filter(
                (i) => i.conversationStatus?.toLowerCase() === s
            );
        }
        if (initiatedFromFilter) {
            const init = initiatedFromFilter.toLowerCase();
            result = result.filter(
                (i) => i.initiatedFrom?.toLowerCase() === init
            );
        }
        if (awaitingReplyOnly) {
            result = result.filter((i) => i.awaitingGroundTeamResponse);
        }
        if (arrivingSoonOnly) {
            result = result.filter((i) =>
                checkIsArrivingSoon(i.arrivalTimeUtc)
            );
        }
        return result;
    }, [
        items,
        unreadOnly,
        searchFlightQuery,
        statusFilter,
        initiatedFromFilter,
        awaitingReplyOnly,
        arrivingSoonOnly,
    ]);

    const pagedItems = useMemo(() => {
        if (pagination.pageSize <= 0) return filteredItems;
        const start = (pagination.currentPage - 1) * pagination.pageSize;
        return filteredItems.slice(start, start + pagination.pageSize);
    }, [filteredItems, pagination.currentPage, pagination.pageSize]);

    const totalFiltered = filteredItems.length;

    useEffect(() => {
        if (loadStatus === 'failed' && error) {
            console.error('Active conversations error:', error);
            const timeoutId = setTimeout(() => {
                dispatch(loadActiveConversations());
            }, 1000);
            return () => clearTimeout(timeoutId);
        }
    }, [loadStatus, error, dispatch]);

    const {
        value: transferConversation,
        openDialog: openTransferDialog,
        isOpen: transferDialogOpen,
        onOpenChangeCallback: onTransferDialogOpenChange,
    } = useValueDialog<IBrokerConnectionActiveConversationsData>();

    return (
        <div className={styles.root}>
            <ToastNotification teamId={teamId} />
            <div className={styles.content}>
                <ActiveConversationsToolbar
                    isConnected={isConnected}
                    groupByStatus={preferences.groupByStatus}
                    onGroupByStatusChange={handleGroupByStatusChange}
                    onTransferClick={(x) => openTransferDialog(x)}
                />
                <div className={styles.tableScrollContainer}>
                    <ActiveConversationsTable
                        key={preferencesTeamId ?? 'all'}
                        items={pagedItems}
                        groupByStatus={preferences.groupByStatus}
                        preferences={preferences}
                        setPreferences={setPreferences}
                        preferencesLoaded={preferencesLoaded}
                        onConversationNotesClick={handleConversationNotesClick}
                    />
                </div>
                <ConversationNotesDialog
                    open={notesModalOpen}
                    notes={conversationNotes.data}
                    loading={conversationNotes.loading}
                    error={conversationNotes.error}
                    currentUserId={teamsContext?.user?.id}
                    flightDetails={selectedConversation}
                    onClose={() => setNotesModalOpen(false)}
                    onSubmit={handleAddConversationNote}
                    onDelete={handleDeleteConversationNote}
                />
                <TransferConversationDialog
                    open={transferDialogOpen}
                    conversationData={transferConversation}
                    onOpenChange={onTransferDialogOpenChange}
                />
                <div className={styles.pagination}>
                    <PaginationBar
                        currentPage={pagination.currentPage}
                        pageSize={pagination.pageSize}
                        totalItems={totalFiltered}
                        onChange={(page) => {
                            dispatch(setCurrentPage(page));
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default ActiveConversationsPage;
